import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import PlaceCard from "./PlaceCard";
import { trackEvent } from "../utils/analytics";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Place {
    place_id: string;
    name: string;
    avg_rating: number;
    rate: number;
    address?: string;
    cities?: { id: number; name: string }[];
    city?: { name?: string } | string;
    opening_hours?: string;
    description?: string;
    categories?: { id: number; name: string }[];
    images?: { file_url: string }[];
    review_count: number;
}

interface NormalizedPlace {
    id: string;
    name: string;
    rate: number;
    image_urls: string[];
    address?: string;
    city?: string;
    opening_hours?: string;
    description?: string;
    review_count: number;
}

interface PlacesSectionProps {
    title: string;
    category: string;
    city?: string;
    hideIfEmpty?: boolean;
    showArrowsOnHover?: boolean;

}

const PlaceCardSkeleton: React.FC = () => (
    <div className="w-64 flex-shrink-0 bg-white border border-gray rounded-lg overflow-hidden">
        <div className="w-full h-48 bg-gray-200 animate-pulse"></div>
        <div className="p-3">
            <div className="flex justify-between items-center mb-1">
                <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse"></div>
                <div className="h-4 bg-gray-200 rounded w-8 animate-pulse"></div>
            </div>
            <div className="flex items-center h-4 mt-1">
                <div className="h-3 w-3 bg-gray-200 rounded-full ml-1 animate-pulse"></div>
                <div className="h-3 bg-gray-200 rounded w-3/4 animate-pulse"></div>
            </div>
            <div className="flex items-center h-4 mt-1">
                <div className="h-3 w-3 bg-gray-200 rounded-full ml-1 animate-pulse"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2 animate-pulse"></div>
            </div>
            <div className="mt-2 space-y-1">
                <div className="h-2 bg-gray-200 rounded w-full animate-pulse"></div>
                <div className="h-2 bg-gray-200 rounded w-full animate-pulse"></div>
                <div className="h-2 bg-gray-200 rounded w-2/3 animate-pulse"></div>
            </div>
        </div>
    </div>
);

const PlacesSection: React.FC<PlacesSectionProps> = ({
    title,
    category,
    city,
    hideIfEmpty,
    showArrowsOnHover = false,
}) => {
    const desktopScrollRef = useRef<HTMLDivElement>(null);
    const mobileScrollRef = useRef<HTMLDivElement>(null);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);

    /* ---------------- VIEW ALL LINK ---------------- */
    const viewAllLink = city
        ? `/places/category/${encodeURIComponent(category)}?city=${encodeURIComponent(city)}`
        : `/places/category/${encodeURIComponent(category)}`;

    /* ---------------- FETCH ---------------- */
    const fetchPlaces = async (): Promise<NormalizedPlace[]> => {
        const params = new URLSearchParams({
            category,
            page: "1",
            page_size: "10",
        });
        if (city) params.append("city", city);

        const res = await fetch(`${BASE_URL}/api/places/?${params.toString()}`);
        if (!res.ok) throw new Error(`خطا در دریافت ${title}`);
        const json = await res.json();
        const dataArray: Place[] = Array.isArray(json) ? json : json.data || [];
        return dataArray.map((item) => ({
            id: item.place_id,
            name: item.name,
            rate: item.rate ?? 0,
            image_urls: item.images?.map((img) => img.file_url) || [],
            address: item.address,
            city: item.cities?.[0]?.name ||
                (typeof item.city === "string" ? item.city : item.city?.name) ||
                "",
            opening_hours: item.opening_hours,
            description: item.description,
            review_count: item.review_count,
        }));
    };

    const { data: places = [], isLoading, isError, error } = useQuery({
        queryKey: ["places", category, city ?? "all"],
        queryFn: fetchPlaces,
        staleTime: 5 * 60 * 1000,
    });

    useEffect(() => {
        const scrollContainer = desktopScrollRef.current;
        if (!scrollContainer) return;

        const updateScrollAvailability = () => {
            const containerBounds = scrollContainer.getBoundingClientRect();
            const cards = Array.from(scrollContainer.children) as HTMLElement[];

            setCanScrollPrev(
                cards.some((card) => card.getBoundingClientRect().right > containerBounds.right + 1)
            );
            setCanScrollNext(
                cards.some((card) => card.getBoundingClientRect().left < containerBounds.left - 1)
            );
        };

        const animationFrame = window.requestAnimationFrame(updateScrollAvailability);
        const resizeObserver = new ResizeObserver(updateScrollAvailability);

        resizeObserver.observe(scrollContainer);
        scrollContainer.addEventListener("scroll", updateScrollAvailability, { passive: true });
        window.addEventListener("resize", updateScrollAvailability);

        return () => {
            window.cancelAnimationFrame(animationFrame);
            resizeObserver.disconnect();
            scrollContainer.removeEventListener("scroll", updateScrollAvailability);
            window.removeEventListener("resize", updateScrollAvailability);
        };
    }, [places, isLoading]);

    if (hideIfEmpty && !isLoading && (isError || places.length === 0)) {
        return null;
    }

    /* ---------------- SCROLL ---------------- */
    const scrollPrev = () =>
        desktopScrollRef.current?.scrollBy({ left: 600, behavior: "smooth" });
    const scrollNext = () =>
        desktopScrollRef.current?.scrollBy({ left: -600, behavior: "smooth" });

  const getArrowClassName = () => {
    const baseClass =
      "flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/80 bg-white/90 text-gray-800 shadow-sm backdrop-blur-sm transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#038545]";

    if (!showArrowsOnHover) {
      return baseClass;
    }

    return `${baseClass} pointer-events-none opacity-0 transition-opacity duration-200 group-hover/rail:pointer-events-auto group-hover/rail:opacity-100 focus-visible:pointer-events-auto focus-visible:opacity-100`;
  };

    /* ---------------- CONTENT ---------------- */
    const renderContent = () => {
        if (isLoading) {
            return Array.from({ length: 5 }).map((_, i) => (
                <PlaceCardSkeleton key={i} />
            ));
        }
        if (isError) {
            return (
                <div className="w-full text-center text-red-500 py-10 font-myYekanRegular">
                    {(error as Error).message}
                </div>
            );
        }
        if (places.length === 0) {
            return (
                <div className="w-full text-center text-gray-400 py-10 font-myYekanRegular">
                    موردی برای نمایش وجود ندارد
                </div>
            );
        }
        return places.map((place) => <PlaceCard key={place.id} place={place} />);
    };

    /* ---------------- RENDER ---------------- */
    return (
        <div className="site-container py-1">
            {/* Header */}
            <div className="flex justify-between items-center mb-4 tablet:px-12 desktop:px-0">
                <h2 className="text-lg tablet:text-xl desktop:text-2xl font-myYekanFaNumExtraBold">
                    {title}
                </h2>
                <Link
                    to={viewAllLink}
                    onClick={() => trackEvent("click_category_view_all", {
                        category_name: category,
                        source: city ? "city" : "home",
                        ...(city ? { city_name: city } : {}),
                    })}
                    className="flex items-center gap-1 text-[12px] text-gray-800 font-myYekanFaNumMedium"
                >
                    مشاهده همه
                    <ChevronLeftIcon className="h-4 w-4" />
                </Link>
            </div>

            {/* Mobile */}
            <div className="tablet:hidden">
                <div
                    ref={mobileScrollRef}
                    className="flex overflow-x-auto space-x-2 rtl:space-x-reverse pb-6 scrollbar-hide"
                >
                    {renderContent()}
                </div>
            </div>

            {/* Desktop */}
            <div className={`hidden tablet:block relative ${showArrowsOnHover ? "group/rail" : ""}`}>
        {canScrollPrev && (
          <button
            onClick={scrollPrev}
            aria-label="مشاهده مکان‌های قبلی"
            className={`${getArrowClassName()} absolute right-3 top-1/2 z-10 -translate-y-1/2`}
          >
            <ChevronRightIcon className="h-6 w-6" />
          </button>
        )}
                <div
                    ref={desktopScrollRef}
                    className="flex overflow-x-auto space-x-2 rtl:space-x-reverse pb-6 scrollbar-hide"
                >
                    {renderContent()}
                </div>
        {canScrollNext && (
          <button
            onClick={scrollNext}
            aria-label="مشاهده مکان‌های بعدی"
            className={`${getArrowClassName()} absolute left-3 top-1/2 z-10 -translate-y-1/2`}
          >
            <ChevronLeftIcon className="h-6 w-6" />
          </button>
        )}
            </div>

            <style>{`
                .scrollbar-hide::-webkit-scrollbar { display: none; }
                .scrollbar-hide { scrollbar-width: none; }
            `}</style>
        </div>
    );
};

export default PlacesSection;
